import { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import MobileNav from "./MobileNav";
import Footer from "./Footer";
import RadioPlayer from "./RadioPlayer";
import EncoreWidget from "./EncoreWidget";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Bell, X, Megaphone } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";

const NO_FOOTER_ROUTES = ["/dashboard", "/admin"];

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const showFooter = !NO_FOOTER_ROUTES.some((r) => pathname.startsWith(r));

  const [banner, setBanner] = useState(null);         // banner text
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // PWA install prompt (Android/Chrome)
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(
    () => localStorage.getItem("pwa-install-dismissed") === "1"
  );

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    getDoc(doc(db, "siteSettings", "global"))
      .then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          if (d.bannerActive && d.platformBanner) {
            setBanner(d.platformBanner);
          }
        }
      })
      .catch(() => {});
  }, []);

  const showBanner = banner && !bannerDismissed;
  const showInstallBar = installPrompt && !installDismissed;

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setInstallPrompt(null);
    else dismissInstall();
  };

  const dismissInstall = () => {
    setInstallDismissed(true);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  return (
    <div className="min-h-screen text-white font-body" style={{ background: "#0a0a0a" }}>
      {/* Platform-wide announcement banner */}
      {showBanner && (
        <div
          data-testid="platform-banner"
          className="fixed top-0 left-0 right-0 z-[100] flex items-center gap-3 px-4 py-2.5"
          style={{ background: "linear-gradient(90deg, #B026FF, #7C3AED)" }}
        >
          <Megaphone size={14} className="text-white flex-shrink-0" />
          <p className="text-white text-xs sm:text-sm font-medium flex-1 text-center">{banner}</p>
          <button
            onClick={() => setBannerDismissed(true)}
            data-testid="banner-dismiss-btn"
            className="text-white/70 hover:text-white transition-colors flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* PWA Install prompt — shown on Android/Chrome when app is installable */}
      {showInstallBar && (
        <div
          data-testid="pwa-install-bar"
          className="fixed bottom-20 left-4 right-4 z-[99] flex items-center gap-3 rounded-2xl px-4 py-3 border border-[#00ff77]/20 shadow-xl md:left-auto md:right-6 md:w-80"
          style={{ background: "#111" }}
        >
          <img src="/logo192.png" alt="StagePass" className="w-10 h-10 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-bold">Install StagePass</p>
            <p className="text-zinc-500 text-[10px]">Add to home screen for the full app experience</p>
          </div>
          <button
            data-testid="pwa-install-btn"
            onClick={handleInstall}
            className="flex-shrink-0 bg-[#00ff77] text-black text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#00e66b] transition-colors"
          >
            Install
          </button>
          <button
            onClick={dismissInstall}
            data-testid="pwa-dismiss-btn"
            className="flex-shrink-0 text-zinc-600 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <Sidebar bannerShowing={showBanner} />
      <MobileNav bannerShowing={showBanner} />

      {/* Main content */}
      <main
        className="md:ml-60 pt-14 md:pt-0 pb-16 md:pb-0 min-h-screen flex flex-col"
        style={{ paddingTop: showBanner ? "calc(2.5rem + 3.5rem)" : undefined }}
      >
        {/* Top bar for desktop */}
        <div
          className="hidden md:flex items-center justify-end px-6 py-3 border-b border-white/5 flex-shrink-0"
          style={{ marginTop: showBanner ? "2.5rem" : 0 }}
        >
          {user ? (
            <button data-testid="notification-bell-desktop" className="relative p-2 text-zinc-400 hover:text-white transition-colors rounded-xl hover:bg-white/5">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#00ff77] border border-[#0a0a0a]" />
            </button>
          ) : (
            <div className="w-8" />
          )}
        </div>

        {/* Page content */}
        <div className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>

        {/* Persistent player - above footer */}
        <RadioPlayer />

        {showFooter && <Footer />}
      </main>

      {/* Encore AI Widget - floating */}
      <EncoreWidget />
    </div>
  );
}
